import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Math.floor((new Date() - new Date(dateStr)) / 60000);
  if (diff < 1) return '방금 전';
  if (diff < 60) return `${diff}분 전`;
  const h = Math.floor(diff / 60);
  return `${h}시간 전`;
};

const CALL_TYPES = {
  call: { label: '직원호출', icon: '🙋', color: 'var(--primary)' },
  water: { label: '물', icon: '💧', color: 'var(--info)' },
  tissue: { label: '냅킨', icon: '🧻', color: 'var(--success)' },
  plate: { label: '접시', icon: '🍽️', color: 'var(--warning)' },
  custom: { label: '기타', icon: '💬', color: 'var(--text-secondary)' },
};

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [660, 880, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      const t = ctx.currentTime + i * 0.15;
      gain.gain.setValueAtTime(0.3, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      osc.start(t);
      osc.stop(t + 0.2);
    });
  } catch {}
};

let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
};

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconXMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconBell = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast ${t.type}`}>
        <span className="toast-icon">{t.type === 'success' ? <IconCheck /> : t.type === 'error' ? <IconXMark /> : <IconBell />}</span>
        <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
      </div>
    ))}
  </div>
);

const StaffCallPage = () => {
  const { user } = useAuth();
  const { toasts, addToast } = useToast();
  const storeId = user?.storeId || user?.store_id;

  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'pending' | 'acknowledged'
  const prevCountRef = useRef(0);

  const fetchCalls = useCallback(async () => {
    if (!storeId) {
      setCalls([]);
      setIsLoading(false);
      return;
    }
    try {
      const res = await api.get(`/admin/staff-calls/${storeId}`);
      const data = res.data;
      const list = Array.isArray(data) ? data : data.calls || data.staffCalls || [];
      setCalls(list);
    } catch {
      setCalls([]);
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchCalls();
    const interval = setInterval(fetchCalls, 5000);
    return () => clearInterval(interval);
  }, [fetchCalls]);

  // Sound alert on new pending calls
  useEffect(() => {
    const pendingCount = calls.filter((c) => c.status === 'pending').length;
    if (pendingCount > prevCountRef.current && prevCountRef.current > 0) {
      playBeep();
      addToast('새로운 직원 호출이 있습니다!', 'info');
    }
    prevCountRef.current = pendingCount;
  }, [calls, addToast]);

  const handleAcknowledge = useCallback(async (callId) => {
    // Optimistic update
    setCalls((prev) => prev.map((c) => (c._id || c.id) === callId ? { ...c, status: 'acknowledged' } : c));
    try {
      if (storeId) {
        await api.put(`/admin/staff-calls/${callId}/acknowledge`);
      }
      addToast('호출이 확인되었습니다.', 'success');
    } catch {
      // Revert
      setCalls((prev) => prev.map((c) => (c._id || c.id) === callId ? { ...c, status: 'pending' } : c));
      addToast('확인 처리에 실패했습니다.', 'error');
    }
  }, [storeId, addToast]);

  const filteredCalls = filter === 'all' ? calls : calls.filter((c) => c.status === filter);
  const pendingCount = calls.filter((c) => c.status === 'pending').length;

  if (isLoading) {
    return (
      <div className="page-container">
        <div style={{ marginBottom: 28 }}>
          <div className="skeleton-line h-24 w-60" />
          <div className="skeleton-line w-40" style={{ height: 12, marginTop: 8 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {[0,1,2,3].map((i) => (
            <div key={i} className="skeleton-card animate-slide-in-right" style={{ animationDelay: `${i * 50}ms`, padding: 20 }}>
              <div style={{ display: 'flex', gap: 14 }}>
                <div className="skeleton-circle" style={{ width: 44, height: 44 }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton-line w-60" />
                  <div className="skeleton-line w-40" style={{ height: 10 }} />
                  <div className="skeleton-line w-40" style={{ height: 10 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Toast toasts={toasts} />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">직원 호출</h1>
          <p className="page-subtitle">실시간 직원 호출 현황 · 5초마다 자동 새로고침</p>
        </div>
        {pendingCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--danger-light)', padding: '8px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(240,68,82,0.2)' }}>
            <span style={{ animation: 'pulse-dot 1.5s infinite', background: 'var(--danger)', width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--danger)' }}>
              대기 {pendingCount}건
            </span>
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="tab-bar">
        {[
          { key: 'all', label: '전체' },
          { key: 'pending', label: '대기중' },
          { key: 'acknowledged', label: '확인됨' },
        ].map((tab) => {
          const count = tab.key === 'all' ? calls.length : calls.filter((c) => c.status === tab.key).length;
          return (
            <button
              key={tab.key}
              className={`tab-btn ${filter === tab.key ? 'active' : ''}`}
              onClick={() => setFilter(tab.key)}
            >
              {tab.label}
              {count > 0 && (
                <span style={{
                  background: filter === tab.key ? 'var(--primary)' : 'var(--border)',
                  color: filter === tab.key ? '#fff' : 'var(--text-secondary)',
                  borderRadius: 10, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700,
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Calls list */}
      {filteredCalls.length === 0 ? (
        <div className="card" style={{ marginTop: 8 }}>
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <h3>호출이 없습니다</h3>
            <p>고객이 직원을 호출하면 여기에 표시됩니다.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16, marginTop: 8 }}>
          {filteredCalls
            .sort((a, b) => {
              // Pending first, then by time desc
              if (a.status === 'pending' && b.status !== 'pending') return -1;
              if (a.status !== 'pending' && b.status === 'pending') return 1;
              return new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at);
            })
            .map((call, idx) => {
              const id = call._id || call.id;
              const type = CALL_TYPES[call.callType || call.call_type] || CALL_TYPES.call;
              const isPending = call.status === 'pending';
              const tableNum = call.tableNumber || call.table_number;

              return (
                <div
                  key={id}
                  className={`animate-slide-in-right ${isPending ? 'staff-call-pending' : ''}`}
                  style={{
                    background: isPending ? 'var(--surface)' : 'var(--bg)',
                    borderRadius: 'var(--radius-lg)',
                    border: isPending ? `2px solid ${type.color}` : '1px solid var(--border)',
                    padding: '20px',
                    boxShadow: isPending ? '0 4px 16px rgba(0,0,0,0.08)' : 'none',
                    opacity: isPending ? 1 : 0.65,
                    transition: 'all 0.2s ease',
                    animationDelay: `${idx * 50}ms`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    {/* Icon */}
                    <div style={{
                      width: 44, height: 44, borderRadius: 'var(--radius-sm)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.4rem', flexShrink: 0,
                      background: isPending ? `${type.color}15` : 'var(--border)',
                    }}>
                      {type.icon}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                          테이블 {tableNum}번
                        </span>
                        <span className={`badge ${isPending ? 'badge-pending' : 'badge-served'}`}>
                          {isPending ? '대기중' : '확인됨'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: type.color, fontWeight: 600, marginBottom: 4 }}>
                        {type.label}
                      </div>
                      {(call.message || call.note) && (
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                          {call.message || call.note}
                        </div>
                      )}
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
                        {timeAgo(call.createdAt || call.created_at)}
                      </div>
                    </div>
                  </div>

                  {/* Acknowledge button */}
                  {isPending && (
                    <button
                      className="btn btn-primary w-full"
                      style={{ marginTop: 14 }}
                      onClick={() => handleAcknowledge(id)}
                    >
                      <IconCheck /> 확인
                    </button>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default StaffCallPage;
